import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { CampaignDraft, CampaignDraftItem, CampaignPanelState, CampaignPanelType, ExtensionRules, PanelParams } from "./types";

interface IProps {
  state: CampaignPanelState;
  draft: CampaignDraft;
  onChange: (draft: CampaignDraft) => void;
}

const blockSx = { border: "1px solid rgba(0, 0, 0, 0.23)", borderRadius: "8px", p: 2, display: "flex", flexDirection: "column", gap: 1 };

export function stateLabel(t: CampaignPanelType): string {
  if (t.state === "ACTIVE") {
    return t.startDate ? `Ativo desde ${new Date(t.startDate).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}` : "Ativo";
  }
  if (t.state === "ARMED") return "Armado: aguarda a conclusão da implantação";
  return "Desligado";
}

export function offMessage(t: CampaignPanelType | null): string {
  if (!t) return "";
  if (t.type !== "contributario") return "Informe o motivo. Ele fica no histórico de configuração do app.";
  if (t.waitingCount === null) {
    return "Não foi possível contar quem está em espera agora. Quem estiver em espera será liberado sem decisão.";
  }
  if (t.waitingCount === 0) return "Ninguém está em espera por esta campanha.";
  return t.waitingCount === 1
    ? "1 pessoa em espera será liberada sem decisão."
    : `${t.waitingCount} pessoas em espera serão liberadas sem decisão.`;
}

export function onMessage(t: CampaignPanelType | null): string {
  const n = t?.turnOnCheck?.wouldHold;
  if (n === null || n === undefined) return "Não foi possível calcular quantas pessoas passam a ficar Em espera pela campanha.";
  return n === 1
    ? "1 pessoa passa a ficar Em espera pela campanha a partir desta noite."
    : `${n} pessoas passam a ficar Em espera pela campanha a partir desta noite.`;
}

export function cadenceLine(step: { recipient: string; entryAfterDays: number; resendEveryDays: number; maxResends: number }): string {
  const envios = step.maxResends + 1;
  const quem =
    step.recipient === "BENEFICIARY" ? "ao colaborador" : step.recipient === "BUYER_ORGANIZATION_USERS" ? "ao RH" : `a ${step.recipient}`;
  const dias = step.resendEveryDays === 1 ? "1 dia" : `${step.resendEveryDays} dias`;
  return `A partir do dia ${step.entryAfterDays}: ${envios} ${envios === 1 ? "envio" : "envios"} ${quem}, a cada ${dias}`;
}

/** Cabeçalho comum aos blocos: título, estado e interruptor. Desligar é sempre permitido; só ligar depende dos motivos de bloqueio. */
function BlockHeader({
  t,
  item,
  blockReasons,
  onToggle,
}: {
  t: CampaignPanelType;
  item: CampaignDraftItem;
  blockReasons: (string | null)[];
  onToggle: (on: boolean) => void;
}) {
  // O bloqueio só vale para quem está desligado no banco e no rascunho: desligar no rascunho não tranca a volta.
  const reasons = t.isActive || item.isActive ? [] : blockReasons.filter((r): r is string => !!r);
  const describedBy = `bloqueio-${t.type}`;
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" fontWeight={600}>{t.label}</Typography>
        <Chip size="small" label={stateLabel(t)} />
      </Box>
      <FormControlLabel
        label={`Ligar ${t.label}`}
        control={
          <Switch
            checked={item.isActive}
            disabled={reasons.length > 0}
            onChange={(e) => onToggle(e.target.checked)}
            inputProps={{ "aria-describedby": reasons.length > 0 ? describedBy : undefined }}
          />
        }
      />
      {reasons.length > 0 && (
        <Box id={describedBy}>
          {reasons.map((r) => (
            <Typography key={r} variant="body2" color="text.secondary">{r}</Typography>
          ))}
        </Box>
      )}
    </>
  );
}

const MOTIVO_PRIMEIRA_ATIVACAO = "Definido na primeira ativação; não pode ser alterado depois.";

/**
 * Travado depois da primeira ativação (startDate preenchido): o backend recusa a mudança. `alwaysLockedReason`
 * trava em qualquer estado, com o motivo dado (contributário: a campanha só começa depois da implantação).
 */
function WaitForImplantation({
  t,
  item,
  onParams,
  alwaysLockedReason,
}: {
  t: CampaignPanelType;
  item: CampaignDraftItem;
  onParams: (patch: Partial<PanelParams>) => void;
  alwaysLockedReason?: string;
}) {
  const locked = !!alwaysLockedReason || t.startDate !== null;
  const describedBy = `aguardar-implantacao-${t.type}`;
  return (
    <>
      <FormControlLabel
        label="Aguardar implantação concluída"
        control={
          <Checkbox
            checked={!!alwaysLockedReason || item.params.waitForImplantation !== false}
            disabled={locked}
            onChange={(e) => onParams({ waitForImplantation: e.target.checked })}
            inputProps={{ "aria-describedby": locked ? describedBy : undefined }}
          />
        }
      />
      {locked && (
        <Typography id={describedBy} variant="body2" color="text.secondary">
          {alwaysLockedReason ?? MOTIVO_PRIMEIRA_ATIVACAO}
        </Typography>
      )}
    </>
  );
}

function ContributarioBlock({
  t,
  item,
  onToggle,
  onParams,
}: {
  t: CampaignPanelType;
  item: CampaignDraftItem;
  onToggle: (on: boolean) => void;
  onParams: (patch: Partial<PanelParams>) => void;
}) {
  return (
    <Box component="section" aria-label={t.label} sx={blockSx}>
      <BlockHeader
        t={t}
        item={item}
        blockReasons={[
          t.eligible ? null : "Só liga em contrato compulsório com a contribuição do empregado marcada nas regras do contrato.",
          t.turnOnBlocked,
        ]}
        onToggle={onToggle}
      />
      <WaitForImplantation t={t} item={item} onParams={onParams} alwaysLockedReason="A campanha de contributários só começa depois da implantação concluída." />
      <Typography variant="subtitle2">Cadência (padrão)</Typography>
      {t.defaultCadence.map((step, i) => (
        <Typography key={`${i}-${step.entryAfterDays}`} variant="body2">{cadenceLine(step)}</Typography>
      ))}
    </Box>
  );
}

/** Códigos da spec §4.2; o código da plataforma não declara quais valem (candidate-filter.ts): a lista é do contrato. */
const TERMINATION_REASONS = [
  { code: "02", label: "Motivo 02 (sem justa causa)" },
  { code: "03", label: "Motivo 03" },
  { code: "04", label: "Motivo 04" },
  { code: "06", label: "Motivo 06" },
  { code: "33", label: "Motivo 33 (acordo, art. 484-A)" },
];

const EMPTY_RULES: ExtensionRules = { eligibleTerminationReasons: [], minContributionMonths: null, windowDays: null };

export function extensionBlockReason(ext: NonNullable<CampaignPanelType["extension"]>): string | null {
  if (!ext.engineAvailable) return "O motor da extensão ainda não foi publicado: o interruptor fica desativado. As regras abaixo já ficam gravadas.";
  if (ext.kit.status === "CATALOG_UNAVAILABLE") return "O catálogo de kits ainda não foi publicado: a extensão não liga.";
  if (ext.kit.status === "NONE") return "Não há kit de formulários para esta operadora e este produto: a extensão não liga.";
  return null;
}

/** Aviso de quem já está ligada: afirma só o que a tela sabe (o interruptor não está desativado). */
export function extensionLiveWarning(ext: NonNullable<CampaignPanelType["extension"]>): string | null {
  if (!ext.engineAvailable) return "Ligada, mas o motor da extensão ainda não foi publicado.";
  if (ext.kit.status === "CATALOG_UNAVAILABLE") return "Ligada, mas o catálogo de kits está indisponível.";
  if (ext.kit.status === "NONE") return "Ligada, mas não há kit para este contrato.";
  return null;
}

/** Vazio é nulo; só inteiro maior ou igual a 1 vale. Qualquer outra digitação é ignorada (undefined). */
function positiveIntOrNull(raw: string): number | null | undefined {
  if (raw.trim() === "") return null;
  return /^[1-9]\d*$/.test(raw.trim()) ? Number(raw) : undefined;
}

function ExtensionBlock({
  t,
  item,
  onToggle,
  onParams,
  onRules,
}: {
  t: CampaignPanelType;
  item: CampaignDraftItem;
  onToggle: (on: boolean) => void;
  onParams: (patch: Partial<PanelParams>) => void;
  onRules: (rules: ExtensionRules) => void;
}) {
  const ext = t.extension as NonNullable<CampaignPanelType["extension"]>;
  const rules = item.extensionRules ?? ext.rules ?? EMPTY_RULES;
  // Motivos gravados que a lista não conhece continuam à vista e removíveis.
  const knownCodes = TERMINATION_REASONS.map((r) => r.code);
  const reasonOptions = [
    ...TERMINATION_REASONS,
    ...rules.eligibleTerminationReasons.filter((c) => !knownCodes.includes(c)).map((c) => ({ code: c, label: `Motivo ${c} (fora da lista)` })),
  ];
  const setNumber = (field: "minContributionMonths" | "windowDays", raw: string) => {
    const value = positiveIntOrNull(raw);
    if (value !== undefined) onRules({ ...rules, [field]: value });
  };
  const kitLine =
    ext.kit.status === "FOUND" ? `Kit: ${ext.kit.kits.map((k) => k.name).join(", ")}` : ext.kit.status === "NONE" ? "Kit: nenhum" : "Kit: catálogo indisponível";
  const liveWarning = item.isActive ? extensionLiveWarning(ext) : null;
  const setReason = (code: string, on: boolean) =>
    onRules({
      ...rules,
      eligibleTerminationReasons: on
        ? [...rules.eligibleTerminationReasons.filter((c) => c !== code), code].sort()
        : rules.eligibleTerminationReasons.filter((c) => c !== code),
    });
  const contributes = ext.contractContributes === null ? "não informado" : ext.contractContributes ? "sim" : "não";
  const contributionLine = ext.planContribution
    ? `Contribuição por plano: ${ext.planContribution.contributing} de ${ext.planContribution.total} planos contribuem`
    : `Contrato contributário: ${contributes}`;

  return (
    <Box component="section" aria-label={t.label} sx={blockSx}>
      <BlockHeader t={t} item={item} blockReasons={[extensionBlockReason(ext), t.turnOnBlocked]} onToggle={onToggle} />
      {liveWarning && <Typography variant="body2" color="warning.main">{liveWarning}</Typography>}
      <Typography variant="body2">{kitLine}</Typography>
      {ext.kit.status === "FOUND" && ext.kit.onlyGeneric && (
        <Typography variant="body2" color="warning.main">Kit genérico: a operadora ainda não tem formulário próprio.</Typography>
      )}
      <FormControlLabel
        label="Enviar automaticamente"
        control={<Checkbox checked={item.params.sendAutomatically !== false} onChange={(e) => onParams({ sendAutomatically: e.target.checked })} />}
      />
      <WaitForImplantation t={t} item={item} onParams={onParams} />
      <Typography variant="subtitle2">Quem tem direito</Typography>
      <Typography variant="body2">{contributionLine}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {reasonOptions.map((r) => (
          <FormControlLabel
            key={r.code}
            label={r.label}
            control={<Checkbox checked={rules.eligibleTerminationReasons.includes(r.code)} onChange={(e) => setReason(r.code, e.target.checked)} />}
          />
        ))}
      </Box>
      {item.isActive && rules.eligibleTerminationReasons.length === 0 && (
        <Typography variant="body2" color="warning.main">Nenhum motivo de desligamento marcado: ninguém recebe a oferta.</Typography>
      )}
      <TextField
        type="number"
        label="Mínimo de meses de contribuição"
        inputProps={{ min: 1, step: 1 }}
        value={rules.minContributionMonths ?? ""}
        onChange={(e) => setNumber("minContributionMonths", e.target.value)}
      />
      <TextField
        type="number"
        label="Janela da oferta (dias)"
        inputProps={{ min: 1, step: 1 }}
        value={rules.windowDays ?? ""}
        onChange={(e) => setNumber("windowDays", e.target.value)}
      />
    </Box>
  );
}

/** Spec §4: um bloco por tipo oferecido pelo template, no painel do app Campanhas. */
export function CampaignTypesPanel({ state, draft, onChange }: IProps) {
  // O tipo fica guardado depois de fechar, para o título não piscar vazio durante a saída do diálogo.
  const [pendingOff, setPendingOff] = useState<CampaignPanelType | null>(null);
  const [offOpen, setOffOpen] = useState(false);
  const [reason, setReason] = useState("");
  // Mesmo padrão do diálogo de desligar: o tipo fica guardado para o título não piscar vazio na saída.
  const [pendingOn, setPendingOn] = useState<CampaignPanelType | null>(null);
  const [onOpen, setOnOpen] = useState(false);

  const update = (type: string, patch: Partial<CampaignDraftItem>) => onChange({ ...draft, [type]: { ...draft[type], ...patch } });
  const updateParams = (type: string, patch: Partial<PanelParams>) => update(type, { params: { ...draft[type].params, ...patch } });

  /** Desligar o que está ligado no banco pede confirmação e motivo (spec §4.1); o resto é direto. */
  function toggle(t: CampaignPanelType, on: boolean) {
    if (on) {
      // Contrato já Em operação: ligar o que está desligado no banco põe gente em espera nesta noite, então pede confirmação (zero liga direto).
      if (!t.isActive && t.turnOnCheck && t.turnOnCheck.wouldHold !== 0) {
        setPendingOn(t);
        setOnOpen(true);
        return;
      }
      return update(t.type, { isActive: true, reason: null });
    }
    if (t.isActive) {
      setReason("");
      setPendingOff(t);
      setOffOpen(true);
      return;
    }
    update(t.type, { isActive: false, reason: null });
  }

  function confirmOff() {
    if (!pendingOff || !reason.trim()) return;
    update(pendingOff.type, { isActive: false, reason: reason.trim() });
    setOffOpen(false);
  }

  function confirmOn() {
    if (!pendingOn) return;
    update(pendingOn.type, { isActive: true, reason: null });
    setOnOpen(false);
  }

  /** why: backend antigo não manda canEdit; só o falso explícito trava (o comportamento anterior era editar). */
  const readOnly = state.canEdit === false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {readOnly && <Typography variant="body2" color="text.secondary">Só a corretora do contrato altera as campanhas.</Typography>}
      {/* why: fieldset disabled desabilita de uma vez todos os inputs nativos dos blocos (Switch, Checkbox, TextField). */}
      <Box component="fieldset" disabled={readOnly} sx={{ border: 0, p: 0, m: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      {state.types.map((t) => {
        const item = draft[t.type];
        if (!item) return null;
        if (t.type === "contributario") {
          return (
            <ContributarioBlock
              key={t.type}
              t={t}
              item={item}
              onToggle={(on) => toggle(t, on)}
              onParams={(patch) => updateParams(t.type, patch)}
            />
          );
        }
        if (!t.extension) return null;
        return (
          <ExtensionBlock
            key={t.type}
            t={t}
            item={item}
            onToggle={(on) => toggle(t, on)}
            onParams={(patch) => updateParams(t.type, patch)}
            onRules={(rules) => update(t.type, { extensionRules: rules })}
          />
        );
      })}
      </Box>
      <Dialog open={onOpen} onClose={() => setOnOpen(false)} aria-labelledby="campanha-ligar-titulo">
        <DialogTitle id="campanha-ligar-titulo">Ligar {pendingOn?.label}</DialogTitle>
        <DialogContent>
          <Typography>{onMessage(pendingOn)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnOpen(false)}>Não ligar</Button>
          <Button onClick={confirmOn}>Ligar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={offOpen} onClose={() => setOffOpen(false)} aria-labelledby="campanha-desligar-titulo">
        <DialogTitle id="campanha-desligar-titulo">Desligar {pendingOff?.label}</DialogTitle>
        <DialogContent>
          <Typography>{offMessage(pendingOff)}</Typography>
          <TextField fullWidth margin="normal" label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffOpen(false)}>Manter ligado</Button>
          <Button color="error" disabled={!reason.trim()} onClick={confirmOff}>Desligar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
